import React from "react";
import { Server, GitBranch, CloudUpload, ShieldCheck, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Server,
    title: "PeopleSoft Administration",
    body:
      "Day-to-day operations, environment management, performance tuning and patching for HCM, FSCM and Campus Solutions. Web/App domain configuration, Change Assistant, PUM Image refreshes.",
    points: ["PeopleTools 8.40 → 8.62", "Production support & DR", "Performance & monitoring"],
  },
  {
    icon: GitBranch,
    title: "Version Upgrades",
    body:
      "PeopleTools and application upgrades end-to-end — planning, environment provisioning, multiple test passes, cutover and post-go-live stabilization. PUM HRMS / FSCM image upgrades.",
    points: ["PeopleTools major upgrades", "HCM 9.1 → 9.2 / FSCM 9.1 → 9.2", "PUM Image 25 → 47+"],
  },
  {
    icon: CloudUpload,
    title: "Cloud Migrations",
    body:
      "Lift-and-shift and re-architect of PeopleSoft on AWS and Azure. Sizing, high-availability design, secure VPC topology and migration from AIX / Windows to RedHat Linux.",
    points: ["AWS & Azure architecture", "AIX/Windows → RHEL 8", "HA, DR and sizing"],
  },
  {
    icon: ShieldCheck,
    title: "Security, SSO & MFA",
    body:
      "Fluid security setup, role-based access, Single Sign-On with IBM Web Seal / LDAP, Azure MFA and Appsian multi-factor integration. Database upgrades and hardening.",
    points: ["SSO · Azure MFA · Appsian", "Fluid security model", "Oracle 11c → 19c · SQL · DB2"],
  },
];

export const Services = () => {
  return (
    <section id="services" className="relative py-24 md:py-32 bg-white" data-testid="services-section">
      <div className="container-app">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline-strong w-12" />
              <span className="label-eyebrow">02 · Services</span>
            </div>
            <h2
              className="font-serif text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", lineHeight: 1.05, letterSpacing: "-0.01em", fontWeight: 600 }}
              data-testid="services-headline"
            >
              Four practice areas. One delivery standard.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-foreground/70 leading-relaxed" data-testid="services-intro">
              Independent advisory and hands-on delivery across the full
              PeopleSoft lifecycle — from administration of complex
              multi-environment estates to greenfield cloud architecture.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border" data-testid="services-grid">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={i}
                className="group relative bg-white p-8 md:p-10 transition-colors duration-300 hover:bg-[#FBFAF6]"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon className="w-7 h-7 text-accent" strokeWidth={1.4} />
                  <span className="font-mono text-[11px] tracking-[0.2em] text-foreground/40">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-4" style={{ fontWeight: 600 }}>
                  {s.title}
                </h3>
                <p className="text-sm text-foreground/70 leading-relaxed mb-6">{s.body}</p>
                <ul className="space-y-2">
                  {s.points.map((p, j) => (
                    <li key={j} className="flex items-center gap-3 text-xs">
                      <span className="w-3 h-px bg-accent" />
                      <span className="text-foreground/80">{p}</span>
                    </li>
                  ))}
                </ul>
                <ArrowUpRight className="absolute top-8 right-8 w-5 h-5 text-foreground/0 group-hover:text-accent transition-colors" />
              </article>
            );
          })}
        </div>

        {/* Domain pills */}
        <div className="mt-14 flex flex-wrap items-center gap-3" data-testid="services-domains">
          <span className="label-eyebrow mr-2">Domains</span>
          {["Oracle PeopleSoft HCM 9.2", "PeopleSoft FSCM 9.2", "PeopleSoft Campus Solutions 9.2"].map((d) => (
            <span
              key={d}
              className="border border-foreground/20 px-4 py-2 text-xs tracking-wide font-mono uppercase"
            >
              {d}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
